import { useState, useEffect } from 'react'
import { legalService } from '@services/api/legal'

function TermsPage() {
  const [page, setPage] = useState(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    legalService.getPublic('cgu')
      .then(res => { if (res.success) setPage(res.data) })
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [])

  // Nettoie le HTML collé (styles inline, espaces insécables, balises vides)
  // pour que la typographie du thème s'applique uniformément.
  const cleanContent = (html) => {
    if (!html) return ''
    return html
      .replace(/\s*style="[^"]*"/gi, '')              // retire les styles inline (couleurs/fonds)
      .replace(/&nbsp;/gi, ' ')                        // espaces insécables → espaces normaux
      .replace(/<(h[1-6]|p)>\s*(<br\s*\/?>)?\s*<\/\1>/gi, '') // supprime les titres/paragraphes vides
  }

  // Contenu édité depuis le back-office (prioritaire)
  if (loaded && page?.content) {
    return (
      <div className="min-h-screen bg-[#fefcf8]">
        <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-8">
          <div className="bg-gradient-to-r from-[#001F3F] to-[#003366] rounded-2xl p-8 md:p-12 mb-8 text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{page.title || "Conditions Générales d'Utilisation"}</h1>
            {page.updated_at && (
              <p className="text-lg">Dernière mise à jour : {new Date(page.updated_at).toLocaleDateString('fr-FR')}</p>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
            <div
              className="
                max-w-none text-gray-700 leading-relaxed
                [&_h1]:text-2xl [&_h1]:md:text-3xl [&_h1]:font-bold [&_h1]:text-[#001F3F] [&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:first:mt-0
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-[#001F3F] [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-gray-100 [&_h2]:first:mt-0
                [&_h3]:text-base [&_h3]:font-medium [&_h3]:text-gray-800 [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:leading-relaxed
                [&_p]:mb-4 [&_p]:text-gray-700 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1.5
                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1.5
                [&_li]:text-gray-700 [&_li]:leading-relaxed [&_li]:pl-1 [&_li]:marker:text-[#D4AF37]
                [&_a]:text-[#D4AF37] [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2
                [&_strong]:text-[#001F3F] [&_strong]:font-semibold
                [&_blockquote]:border-l-4 [&_blockquote]:border-[#D4AF37] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-4
                [&_img]:rounded-lg [&_img]:my-4 [&_img]:max-w-full
                [&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_.ql-align-justify]:text-justify
                [&_.ql-indent-1]:pl-8 [&_.ql-indent-2]:pl-16 [&_.ql-indent-3]:pl-24
              "
              dangerouslySetInnerHTML={{ __html: cleanContent(page.content) }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Repli : contenu statique par défaut
  return (
    <div className="min-h-screen bg-[#fefcf8]">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#001F3F] to-[#003366] rounded-2xl p-8 md:p-12 mb-8 text-white">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Conditions Générales d'Utilisation</h1>
          <p className="text-lg">Dernière mise à jour : 26 novembre 2025</p>
        </div>

        {/* Contenu */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">1. Acceptation des conditions</h2>
            <p className="text-gray-700 leading-relaxed">
              En accédant et en utilisant la plateforme KUDHI, vous acceptez d'être lié par ces Conditions 
              Générales d'Utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser nos services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">2. Description du service</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              KUDHI est une plateforme de mise en relation en ligne qui permet aux utilisateurs de :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Mettre en relation vendeurs et acheteurs</li>
              <li>Connecter entreprises et particuliers</li>
              <li>Publier des annonces de produits et services</li>
              <li>Rechercher et consulter des annonces</li>
              <li>Communiquer directement avec les autres utilisateurs</li>
              <li>Gérer leur profil et leurs publications</li>
            </ul>
            <p className="text-gray-700 mt-3">
              KUDHI agit uniquement comme plateforme de mise en relation et ne participe pas aux transactions entre utilisateurs.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">3. Compte utilisateur</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-[#001F3F] mb-2">3.1 Inscription</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Vous devez avoir au moins 18 ans pour créer un compte</li>
                  <li>Vous devez fournir des informations exactes et à jour</li>
                  <li>Vous êtes responsable de la confidentialité de votre mot de passe</li>
                  <li>Un compte par utilisateur est autorisé</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#001F3F] mb-2">3.2 Suspension et résiliation</h3>
                <p className="text-gray-700">
                  Nous nous réservons le droit de suspendre ou résilier votre compte en cas de violation 
                  de ces conditions, d'activité frauduleuse ou de comportement inapproprié.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">4. Publication d'annonces</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-[#001F3F] mb-2">4.1 Contenu autorisé</h3>
                <p className="text-gray-700 mb-2">Vous pouvez publier des annonces pour :</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Produits d'occasion ou neufs légaux</li>
                  <li>Services légaux et autorisés</li>
                  <li>Biens immobiliers</li>
                  <li>Véhicules avec documents en règle</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#001F3F] mb-2">4.2 Contenu interdit</h3>
                <p className="text-gray-700 mb-2">Il est strictement interdit de publier :</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Produits contrefaits ou volés</li>
                  <li>Armes, drogues ou substances illégales</li>
                  <li>Contenu pornographique, violent ou haineux</li>
                  <li>Fausses annonces ou escroqueries</li>
                  <li>Animaux protégés ou interdits</li>
                  <li>Médicaments sans autorisation</li>
                  <li>Services financiers non réglementés</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#001F3F] mb-2">4.3 Responsabilité du contenu</h3>
                <p className="text-gray-700">
                  Vous êtes seul responsable du contenu de vos annonces. Vous garantissez que vos annonces 
                  sont conformes aux lois en vigueur et ne violent aucun droit de propriété intellectuelle.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">5. Transactions entre utilisateurs</h2>
            <div className="space-y-3">
              <p className="text-gray-700">
                KUDHI n'est pas partie prenante des transactions entre acheteurs et vendeurs. Nous ne sommes 
                pas responsables de :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>La qualité, la sécurité ou la légalité des produits</li>
                <li>La véracité des annonces</li>
                <li>La capacité des vendeurs à vendre</li>
                <li>La capacité des acheteurs à payer</li>
                <li>Les litiges entre utilisateurs</li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-yellow-900 font-semibold">⚠️ Important :</p>
                <p className="text-yellow-900">
                  Nous recommandons fortement de rencontrer les vendeurs en personne et d'inspecter 
                  les produits avant tout paiement.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">6. Propriété intellectuelle</h2>
            <p className="text-gray-700 mb-3">
              Tous les éléments de la plateforme KUDHI (logo, design, code, contenu) sont protégés par 
              les droits de propriété intellectuelle. Vous ne pouvez pas :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Copier, modifier ou distribuer notre contenu sans autorisation</li>
              <li>Utiliser notre marque ou logo sans permission écrite</li>
              <li>Créer des œuvres dérivées de notre plateforme</li>
              <li>Extraire des données de manière automatisée (scraping)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">7. Limitation de responsabilité</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Dans les limites permises par la loi, KUDHI ne sera pas responsable de :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Pertes directes ou indirectes résultant de l'utilisation de la plateforme</li>
              <li>Interruptions de service ou erreurs techniques</li>
              <li>Perte de données ou d'informations</li>
              <li>Actions d'autres utilisateurs</li>
              <li>Contenu publié par les utilisateurs</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">8. Frais et paiements</h2>
            <div className="space-y-3">
              <p className="text-gray-700">
                L'utilisation de base de KUDHI est gratuite. Des frais peuvent s'appliquer pour :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Services premium (mise en avant d'annonces)</li>
                <li>Bannières publicitaires</li>
                <li>Fonctionnalités avancées</li>
              </ul>
              <p className="text-gray-700">
                Les prix sont indiqués en Francs CFA (XOF) et peuvent être modifiés à tout moment.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">9. Conduite des utilisateurs</h2>
            <p className="text-gray-700 mb-3">Vous vous engagez à :</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Respecter les lois ivoiriennes et internationales</li>
              <li>Traiter les autres utilisateurs avec respect</li>
              <li>Ne pas harceler, menacer ou intimider</li>
              <li>Ne pas spammer ou envoyer de messages non sollicités</li>
              <li>Ne pas créer de faux comptes ou usurper l'identité d'autrui</li>
              <li>Signaler tout contenu ou comportement inapproprié</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">10. Modifications des conditions</h2>
            <p className="text-gray-700 leading-relaxed">
              Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications 
              importantes vous seront notifiées par email ou via la plateforme. Votre utilisation continue 
              après modification constitue votre acceptation des nouvelles conditions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">11. Loi applicable et juridiction</h2>
            <p className="text-gray-700 leading-relaxed">
              Ces conditions sont régies par les lois de la République de Côte d'Ivoire. Tout litige sera soumis 
              à la juridiction exclusive des tribunaux d'Abidjan.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#001F3F] mb-4">12. Contact</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Pour toute question concernant ces conditions, contactez-nous :
            </p>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-gray-700"><strong>Email :</strong> legal@kudhi.net</p>
              <p className="text-gray-700"><strong>Téléphone :</strong> +225 07 00 00 00 00</p>
              <p className="text-gray-700"><strong>Adresse :</strong> Grand Bassam, Côte d'Ivoire</p>
            </div>
          </section>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <p className="text-blue-900 font-semibold mb-2">
              En utilisant KUDHI, vous reconnaissez avoir lu, compris et accepté ces Conditions Générales d'Utilisation.
            </p>
            <p className="text-blue-900 text-sm">
              Date d'effet : 26 novembre 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsPage
